import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';
import type { PlatformPromoSettings, PromoRedemptionLedgerRow } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderRadius: '20px',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.06)',
  padding: '28px',
};

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export default function PromoAnalyticsScreen() {
  const { isSuperAdmin } = useAuth();
  const { showAlert } = useModal();

  const [settings, setSettings] = useState<PlatformPromoSettings | null>(null);
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [pauseReason, setPauseReason] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [redemptions, setRedemptions] = useState<PromoRedemptionLedgerRow[]>([]);
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);
  const [loadingRedemptions, setLoadingRedemptions] = useState(true);
  const [settlingId, setSettlingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoadingSettings(true);
    setLoadingRedemptions(true);
    try {
      const [s, r] = await Promise.all([
        api.getSuperadminPromoSettings(),
        api.listSuperadminPromoRedemptions({ onlyOutstanding }),
      ]);
      setSettings(s.settings);
      setBudgetInput(s.settings.dailyPlatformBudgetCents == null
        ? ''
        : (s.settings.dailyPlatformBudgetCents / 100).toString());
      setPauseReason(s.settings.pausedReason || '');
      setRedemptions(r.redemptions);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to load promo analytics', 'error');
    } finally {
      setLoadingSettings(false);
      setLoadingRedemptions(false);
    }
  }, [onlyOutstanding, showAlert]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    refresh();
  }, [isSuperAdmin, refresh]);

  const saveBudget = async () => {
    setSavingSettings(true);
    try {
      const trimmed = budgetInput.trim();
      const dollarsValue = trimmed === '' ? null : Number(trimmed);
      const cents = dollarsValue == null ? null : Math.round(dollarsValue * 100);
      const res = await api.updateSuperadminPromoSettings({ dailyPlatformBudgetCents: cents });
      setSettings(res.settings);
      await showAlert('Saved', cents == null ? 'Daily budget cleared.' : `Daily budget set to ${dollars(cents)}.`, 'success');
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update budget', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const togglePause = async (next: boolean) => {
    setSavingSettings(true);
    try {
      const res = await api.updateSuperadminPromoSettings({
        manuallyPaused: next,
        pausedReason: next ? (pauseReason.trim() || null) : null,
      });
      setSettings(res.settings);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to toggle pause', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const settle = async (row: PromoRedemptionLedgerRow) => {
    setSettlingId(row.redemptionId);
    try {
      const res = await api.settleSuperadminPromoRedemption(row.redemptionId);
      await showAlert(
        res.alreadySettled ? 'Already settled' : 'Settled',
        res.transferId ? `Stripe Transfer ${res.transferId}` : 'No payout needed.',
        'success'
      );
      await refresh();
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to settle', 'error');
    } finally {
      setSettlingId(null);
    }
  };

  const totals = useMemo(() => {
    return redemptions.reduce(
      (acc, row) => ({
        discount: acc.discount + (row.discountCents || 0),
        absorbed: acc.absorbed + row.platformAbsorbedCents,
        owed: acc.owed + (row.settledAt ? 0 : row.pendingVenueReimbursementCents),
        settled: acc.settled + (row.settlementAmountCents || 0),
      }),
      { discount: 0, absorbed: 0, owed: 0, settled: 0 }
    );
  }, [redemptions]);

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Access Denied</h1>
          <p>This page is only available to Super Administrators.</p>
        </div>
      </DashboardLayout>
    );
  }

  const paused = settings?.manuallyPaused;
  const budgetExhausted = settings && settings.dailyPlatformBudgetCents != null
    && settings.dailyPlatformSpentCents >= settings.dailyPlatformBudgetCents;

  return (
    <DashboardLayout>
      <div className="page-header" style={{ marginBottom: '28px' }}>
        <div>
          <h1>Promo Analytics</h1>
          <p>Platform-funded promo safeguards and venue reimbursement ledger.</p>
        </div>
      </div>

      {/* ---- Kill switch + daily budget ---- */}
      <section style={{ ...cardStyle, marginBottom: '24px' }}>
        <div style={{ display: 'grid', gap: '6px', marginBottom: '18px' }}>
          <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gray-500)' }}>
            Platform-funded safeguards
          </span>
          <h2 style={{ margin: 0, fontSize: '24px', letterSpacing: '-0.02em' }}>
            {paused ? 'Paused' : budgetExhausted ? 'Budget exhausted' : 'Active'}
          </h2>
          {loadingSettings ? (
            <span style={{ color: 'var(--gray-500)' }}>Loading…</span>
          ) : (
            <span style={{ color: 'var(--gray-600)' }}>
              {paused
                ? `Manually paused${settings?.pausedReason ? ` — ${settings.pausedReason}` : ''}.`
                : budgetExhausted
                  ? `Today's spend of ${dollars(settings?.dailyPlatformSpentCents)} has hit the cap of ${dollars(settings?.dailyPlatformBudgetCents)}.`
                  : settings?.dailyPlatformBudgetCents == null
                    ? 'No daily budget set — platform-funded promos apply without a global cap.'
                    : `${dollars(settings?.dailyPlatformSpentCents)} of ${dollars(settings?.dailyPlatformBudgetCents)} used today.`}
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
              Daily platform-funded budget ($)
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="No limit"
                style={{ flex: 1 }}
              />
              <button className="btn btn-secondary" onClick={saveBudget} disabled={savingSettings}>
                Save
              </button>
            </div>
            <p style={{ marginTop: '6px', color: 'var(--gray-500)', fontSize: '12px' }}>
              Total $ PickleCourts will absorb per rolling 24h. Leave blank to disable the global cap.
            </p>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
              Pause platform-funded redemptions
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="Reason (optional)"
                disabled={!!paused}
                style={{ flex: 1 }}
              />
              <button
                className="btn"
                style={{
                  background: paused ? '#16a34a' : '#dc2626',
                  color: '#fff',
                  fontWeight: 700,
                }}
                onClick={() => togglePause(!paused)}
                disabled={savingSettings}
              >
                {paused ? 'Resume' : 'Pause now'}
              </button>
            </div>
            <p style={{ marginTop: '6px', color: 'var(--gray-500)', fontSize: '12px' }}>
              Kill switch. Stops all platform-funded discounts from applying until resumed.
            </p>
          </div>
        </div>
      </section>

      {/* ---- Subsidy ledger ---- */}
      <section style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '18px' }}>
          <div style={{ display: 'grid', gap: '6px' }}>
            <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gray-500)' }}>
              Subsidy ledger
            </span>
            <h2 style={{ margin: 0, fontSize: '24px', letterSpacing: '-0.02em' }}>Venue reimbursements</h2>
            <span style={{ color: 'var(--gray-600)' }}>
              {dollars(totals.owed)} outstanding · {dollars(totals.settled)} settled · {dollars(totals.absorbed)} absorbed via reduced fee
            </span>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--gray-700)' }}>
            <input
              type="checkbox"
              checked={onlyOutstanding}
              onChange={(e) => setOnlyOutstanding(e.target.checked)}
            />
            Outstanding only
          </label>
        </div>

        {loadingRedemptions ? (
          <div className="loading" style={{ minHeight: '160px' }}><div className="spinner"></div></div>
        ) : redemptions.length === 0 ? (
          <div style={{
            border: '1px dashed rgba(15, 23, 42, 0.12)',
            borderRadius: '14px',
            padding: '28px',
            color: 'var(--gray-600)',
            textAlign: 'center',
          }}>
            {onlyOutstanding ? 'No outstanding reimbursements.' : 'No redemptions yet.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {redemptions.map((row) => {
              const owed = !row.settledAt && row.pendingVenueReimbursementCents > 0;
              return (
                <article key={row.redemptionId} style={{
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  borderRadius: '14px',
                  padding: '16px 18px',
                  display: 'grid',
                  gap: '10px',
                  background: owed ? '#fffbf3' : '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'grid', gap: '4px' }}>
                      <strong>{row.code} · {row.percentOff}% off</strong>
                      <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>
                        {row.venueName || '(no venue)'} · {row.customerEmail || 'unknown'} · {new Date(row.consumedAt).toLocaleString()}
                      </span>
                    </div>
                    <span style={{
                      alignSelf: 'flex-start',
                      borderRadius: '999px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: row.codeFundedBy === 'platform' ? 'rgba(29, 78, 216, 0.10)' : 'rgba(100, 116, 139, 0.10)',
                      color: row.codeFundedBy === 'platform' ? '#1d4ed8' : '#475569',
                    }}>
                      {row.codeFundedBy === 'platform' ? 'PickleCourts pays' : 'Venue pays'}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                    <Stat label="Discount" value={dollars(row.discountCents)} />
                    <Stat label="Platform absorbed" value={dollars(row.platformAbsorbedCents)} />
                    <Stat label="Owed to venue" value={dollars(row.pendingVenueReimbursementCents)} highlight={owed} />
                    <Stat label="Settled" value={row.settledAt ? dollars(row.settlementAmountCents) : '—'} />
                  </div>

                  {row.stripeTransferId && (
                    <span style={{ color: 'var(--gray-500)', fontSize: '12px' }}>
                      Stripe Transfer: {row.stripeTransferId}
                    </span>
                  )}

                  {owed && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => settle(row)}
                        disabled={settlingId === row.redemptionId || !row.hasConnectedAccount}
                        title={row.hasConnectedAccount ? 'Pay venue via Stripe Transfer' : 'Venue has no Connect account — settle off-platform'}
                      >
                        {settlingId === row.redemptionId
                          ? 'Sending…'
                          : row.hasConnectedAccount
                            ? `Settle ${dollars(row.pendingVenueReimbursementCents)} via Stripe`
                            : 'Off-platform (no Connect)'}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: highlight ? '#b45309' : 'var(--gray-500)' }}>{label}</div>
      <div style={{ marginTop: '2px', fontWeight: 700, color: highlight ? '#b45309' : 'inherit' }}>{value}</div>
    </div>
  );
}
